import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { FormLayout, Checkbox, Form, Button, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { fetchUserAssignedRoles, saveWorkSecurityUserRoles, AssignableRole } from "services/work-security.service";
import { setIsModalxOpen, setDestroy } from "components/modalx/modalx.slice";
import UserSelect from "features/user-select/user-select";

interface WorkSecurityUserRolesModalProps {
  userId?: string;
  userName?: string;
  initialRoleIds?: string[];
  availableRoles: AssignableRole[];
  onSuccess?: () => void;
}

// Stable defaults — inline `= []` creates a new array every render and would
// recreate loadAssignedRoles → re-fire the userId effect → infinite assigned-roles fetches.
const EMPTY_ROLE_IDS: string[] = [];
const EMPTY_ROLES: AssignableRole[] = [];

export default function WorkSecurityUserRolesModal({
  userId,
  userName,
  initialRoleIds = EMPTY_ROLE_IDS,
  availableRoles = EMPTY_ROLES,
  onSuccess,
}: WorkSecurityUserRolesModalProps) {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();

  const [selectedUserId, setSelectedUserId] = useState(userId || "");
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>(initialRoleIds);
  const [loadingAssignedRoles, setLoadingAssignedRoles] = useState(Boolean(userId));
  const [assignedRolesLoadFailed, setAssignedRolesLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const assignedRolesRequestRef = useRef(0);
  // Keep fallback role ids out of loadAssignedRoles deps so a new parent array
  // reference cannot restart the fetch loop.
  const initialRoleIdsRef = useRef(initialRoleIds);
  initialRoleIdsRef.current = initialRoleIds;

  const loadAssignedRoles = useCallback(async (val: string) => {
    const requestId = ++assignedRolesRequestRef.current;

    if (!companyId || !val) {
      setAssignedRolesLoadFailed(false);
      setSelectedRoleIds([]);
      setLoadingAssignedRoles(false);
      return;
    }

    try {
      setLoadingAssignedRoles(true);
      setAssignedRolesLoadFailed(false);
      const data = await fetchUserAssignedRoles(companyId, val);
      if (requestId !== assignedRolesRequestRef.current) return;
      setSelectedRoleIds((data.roles || []).map((item) => item.role.id));
    } catch (err) {
      if (requestId !== assignedRolesRequestRef.current) return;
      console.error("Failed to load user role assignments", err);
      setAssignedRolesLoadFailed(true);
      if (userId && val === userId) {
        setSelectedUserId(userId);
        setSelectedRoleIds(initialRoleIdsRef.current);
      } else {
        setSelectedUserId("");
        setSelectedRoleIds([]);
      }
      setError(
        t("failed_load_user_assignments", { defaultValue: "Failed to load the user's current role assignments." })
      );
    } finally {
      if (requestId === assignedRolesRequestRef.current) {
        setLoadingAssignedRoles(false);
      }
    }
  }, [companyId, t, userId]);

  useEffect(() => {
    if (userId) {
      setSelectedUserId(userId);
      setError("");
      loadAssignedRoles(userId);
    }
  }, [userId, loadAssignedRoles]);

  // Handle selected user change and load its current roles independently of table filters.
  const handleUserChange = async (val: string) => {
    setSelectedUserId(val);
    setError("");
    await loadAssignedRoles(val);
  };

  const handleCheckboxChange = (roleId: string, checked: boolean) => {
    setSelectedRoleIds((currentRoleIds) =>
      checked
        ? currentRoleIds.includes(roleId)
          ? currentRoleIds
          : [...currentRoleIds, roleId]
        : currentRoleIds.filter((id) => id !== roleId)
    );
  };

  const handleFormSubmit = async () => {
    if (loadingAssignedRoles || assignedRolesLoadFailed) return;

    if (!companyId || !selectedUserId) {
      setError(t("user_required", { defaultValue: "Please select a user first." }));
      return;
    }

    try {
      setSaving(true);
      setError("");

      await saveWorkSecurityUserRoles(companyId, selectedUserId, selectedRoleIds);

      // Close modal
      dispatch(setIsModalxOpen(false));
      dispatch(setDestroy());

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error("Failed to save user role assignments", err);
      setError(
        err?.response?.data?.message ||
          t("failed_save_assignments", { defaultValue: "Failed to save user role assignments." })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormLayout>
        {error && <Banner tone="critical">{error}</Banner>}

        {/* 1. Single User Selector */}
        {userId ? (
          <div className="bg-zinc-50 dark:bg-zinc-800/40 p-4 border border-zinc-150 dark:border-zinc-800 rounded-lg">
            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider block mb-1">
              {t("user", { defaultValue: "User" })}
            </span>
            <p className="text-sm font-bold text-zinc-800 dark:text-white">{userName}</p>
          </div>
        ) : (
          <UserSelect
            label="select_user_to_assign"
            value={selectedUserId}
            onChange={handleUserChange}
            isDisable={loadingAssignedRoles || saving}
            showUserEmail
          />
        )}

        {/* 2. Roles Checklist Selector */}
        <div className="mt-4 border-t border-zinc-200 dark:border-zinc-800 pt-4 space-y-2">
          <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider block mb-2">
            {t("select_roles_to_assign", { defaultValue: "Select Roles" })}
          </span>

          <div className="space-y-3 pr-1 md:max-h-[260px] md:overflow-y-auto">
            {availableRoles.length === 0 ? (
              <p className="text-sm text-zinc-400 italic">
                {t("no_roles_available", { defaultValue: "No roles defined yet." })}
              </p>
            ) : (
              availableRoles.map((role) => {
                const isChecked = selectedRoleIds.includes(role.id);
                // Block assigning a worker to a role whose latest document is closed (nothing
                // for them to sign). Already-assigned roles stay enabled so they can be removed.
                const blockedByClosedDocument =
                  !!role.isLatestDocumentClosed && !isChecked;
                return (
                  <div key={role.id} className="flex items-center">
                    <Checkbox
                      label={
                        blockedByClosedDocument ? (
                          <span>
                            {role.name}
                            <span className="ml-2 text-xs text-zinc-400 italic">
                              ({t("latest_document_closed", { defaultValue: "latest document closed" })})
                            </span>
                          </span>
                        ) : (
                          role.name
                        )
                      }
                      checked={isChecked}
                      onChange={(checked) => handleCheckboxChange(role.id, checked)}
                      disabled={
                        saving ||
                        loadingAssignedRoles ||
                        assignedRolesLoadFailed ||
                        !selectedUserId ||
                        blockedByClosedDocument
                      }
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* 3. Footer Action Buttons */}
        <div className="flex justify-end gap-3 mt-4 border-t border-zinc-200 dark:border-zinc-800 pt-4">
          <Button
            onClick={() => {
              dispatch(setIsModalxOpen(false));
              dispatch(setDestroy());
            }}
            disabled={saving}
          >
            {t("cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            variant="primary"
            onClick={handleFormSubmit}
            loading={saving || loadingAssignedRoles}
            disabled={!selectedUserId || loadingAssignedRoles || assignedRolesLoadFailed}
          >
            {t("save", { defaultValue: "Save" })}
          </Button>
        </div>
      </FormLayout>
    </Form>
  );
}
